import React from 'react';
import dayjs from 'dayjs';
import { useSelector, shallowEqual } from 'react-redux';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';

import ReviewBreakdownItem from 'app/components/review/ReviewBreakdownItem';
import ProductPassport from 'app/components/product/ProductPassport';
import PlanInfo from 'app/components/product/PlanInfo';
import { Carousel, List } from 'app/seedly-component-library';
import Avatar from 'app/seedly-component-library/avatar-new';
import Button from 'app/seedly-component-library/button-new';
import formatTimeago from 'app/utils/formatTimeago';
import analytics from 'app/utils/analytics';
import mixpanel, { trackedEvents } from 'app/utils/mixpanel';
import useIsMobile from 'app/utils/hooks/useIsMobile';
import SignUpBonusCard from 'app/components/review/SignUpBonusCard';

import type { RootState } from 'app/reducer';
import { ChevronRight } from 'react-feather';

const CallToActionButtons = ({
  internalLink,
  externalLink,
  analyticLink,
  slug,
  onlyApply = false,
}) => {
  const handleClickExternalLink = evt => {
    evt.preventDefault();
    analytics.trackOutbound({
      url: analyticLink,
      from: 'Pdt Listing',
    });

    const category = analyticLink.split('/')[0].replace(/-/g, ' ');
    const product = analyticLink.split('/')[1].replace(/-/g, ' ');
    mixpanel.track(trackedEvents.Outbound, {
      'Category Name': capitalize(category),
      'Product Name': capitalize(product),
      'Plan Name': '',
    });
    window.open(externalLink, '_blank');
  };

  return (
    <div className="flex flex-col gap-2 items-center">
      {!onlyApply && (
        <a
          href={internalLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            mixpanel.track(trackedEvents.ReviewHome, {
              param: `view_more-${slug.replace(/-/g, '_')}`,
            });
          }}
        >
          <Button>View More</Button>
        </a>
      )}
      {externalLink && (
        <a
          className="hover:underline !text-blue-500"
          href={externalLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClickExternalLink}
        >
          Apply Now
        </a>
      )}
    </div>
  );
};

const CallToActionCarInsuranceButtons = ({
  internalLink,
  externalLink,
  price,
  product,
  onlyApply = false,
}) => {
  return (
    <div className="flex flex-col gap-2 items-center">
      <a
        href={externalLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          analytics.trackApplyButton({
            id: product.id,
            productName: product.name,
            productCategory: product.categorySlug,
            productPrice: price,
          });
        }}
      >
        <Button>
          {price ? `S$ ${price}` : 'Apply Now'}
          {price && <ChevronRight size={18} className="ml-1" />}
        </Button>
      </a>
      {internalLink && !onlyApply && (
        <a
          className="hover:underline !text-blue-500"
          href={internalLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            analytics.trackViewMoreButton({
              id: product.id,
              productName: product.name,
              productCategory: product.categorySlug,
            });
          }}
        >
          View More
        </a>
      )}
    </div>
  );
};

const PlanList = ({ plans, isMobile }) => {
  if (plans.length === 1) {
    return (
      <div className="max-w-full">
        <PlanInfo
          plan={plans[0]}
          alignment={isMobile ? 'vertical' : 'horizontal'}
          showTitle={false}
        />
      </div>
    );
  }

  if (plans.length === 2) {
    return (
      <div className="flex gap-3 items-start w-full">
        {plans.map(plan => {
          return (
            <div
              className="w-full border-neutral-400 border card shadow-none"
              key={plan.name}
            >
              <PlanInfo plan={plan} />
            </div>
          );
        })}
      </div>
    );
  }

  if (plans.length > 2) {
    const slidesToShow = isMobile ? 1.5 : 2.5;
    const slideToScroll = isMobile ? 1 : 2;

    return (
      <div className="w-full">
        <Carousel
          slidesToShow={slidesToShow}
          slideToScroll={slideToScroll}
          hideNavigateButton={isMobile}
          dataSource={plans.map(plan => {
            return (
              <div
                className="border-neutral-400 border card shadow-none h-fit"
                key={plan.name}
              >
                <PlanInfo plan={plan} />
              </div>
            );
          })}
        />
      </div>
    );
  }

  return null;
};

const ProductBreakdownList = ({ ratingBreakdown }) => {
  const dataSource = ratingBreakdown.map(breakdown => {
    const { name, id, rating } = breakdown;
    const fillPercentWidth = (rating / 5) * 100;
    return (
      <ReviewBreakdownItem
        key={id}
        name={name}
        fillPercentWidth={fillPercentWidth}
        rating={rating}
        wrapperStyle={{ marginBottom: '0px' }}
        nameStyle={{ width: '165px', fontSize: '12px' }}
        ratingStyle={{
          marginRight: '15px',
          fontWeight: 'bold',
          fontSize: '12px',
        }}
        barStyle={{
          height: '8px',
        }}
        barFillStyle={{
          height: '8px',
        }}
      />
    );
  });

  return <List dataSource={dataSource} listSpacing="xxs" />;
};

const UserReview = ({ user, review }) => {
  if (!user || !review) return null;

  const formattedReview = `"${review.split(' ').slice(0, 12).join(' ')}..."`;
  const dateAgo = formatTimeago(user.updatedAt, { cutoff: false });

  return (
    <div className="flex gap-3 items-center w-full">
      <Avatar name={user.name} src={user.image} />
      <p className="text-sm text-neutral-500">
        {formattedReview} <span className="font-bold">{dateAgo}</span>
      </p>
    </div>
  );
};

const ProductItemWithDetail = props => {
  const productCategoriesById = useSelector(
    (state: RootState) => state.product.productCategoriesById,
    shallowEqual,
  );
  const isMobile = useIsMobile();
  const { product } = props;
  const { slug: productSlug, categoryId, plans } = product;

  const productInfo = get(product, 'details.info', []).find(
    item => item.name === 'url',
  );
  const externalLink = get(productInfo, 'value', '');
  const categorySlug =
    get(product, 'category', null) ||
    get(productCategoriesById, `[${categoryId}].slug`, null);

  if (!categorySlug) return null;
  const analyticLink = [categorySlug, product.slug].filter(Boolean).join('/');

  const as = `/reviews/${categorySlug}/${productSlug}`;
  const hasPlans = plans && plans.length > 0;
  const ratingBreakdown = get(product, 'ratingBreakdownsCount', []);
  const userReview = get(product, 'review', null);

  const signUpBonusPromoText = get(product, 'details.info', []).find(
    item => item.name === 'signUpBonusPromoText',
  );
  const signUpBonusExpiry = get(product, 'details.info', []).find(
    item => item.name === 'signUpBonusExpiry',
  );
  const showSignUpBonusCard = signUpBonusExpiry
    ? !dayjs(new Date()).isAfter(dayjs(signUpBonusExpiry?.value), 'day')
    : false;

  // get plan price for car insurance
  const isCarInsurance = product?.categorySlug === 'car-insurance';
  let lowestPrice;
  if (isCarInsurance) {
    const planPrice = product?.plans?.map(plan => {
      const price = Number(plan.information?.price);
      return isNaN(price) ? null : price;
    });
    lowestPrice = planPrice.sort()[0];
  }

  return (
    <div className="card h-fit">
      <div className="flex flex-col items-start w-full gap-3">
        <div className="flex justify-between w-full">
          <div className="max-w-full lg:max-w-[410px]">
            <ProductPassport
              onTitleClick={() =>
                analytics.trackProductTitle({
                  id: product.id,
                  productCategory: categorySlug,
                  productName: product.name,
                })
              }
              product={product}
              variant="lg"
            />
          </div>
          {!isMobile &&
            (isCarInsurance ? (
              <CallToActionCarInsuranceButtons
                internalLink={as}
                externalLink={externalLink}
                price={lowestPrice}
                product={product}
              />
            ) : (
              <CallToActionButtons
                internalLink={as}
                externalLink={externalLink}
                analyticLink={analyticLink}
                slug={productSlug}
              />
            ))}
        </div>
        {isMobile &&
          (isCarInsurance ? (
            <div className="w-full flex justify-center">
              <CallToActionCarInsuranceButtons
                internalLink={as}
                externalLink={externalLink}
                price={lowestPrice}
                product={product}
                onlyApply
              />
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <CallToActionButtons
                internalLink={as}
                externalLink={externalLink}
                analyticLink={analyticLink}
                slug={productSlug}
                onlyApply
              />
            </div>
          ))}
        {hasPlans && <PlanList plans={plans} isMobile={isMobile} />}
        {isCarInsurance && (
          <p className="text-neutral-500 text-xs">
            Based on a 35 year old married male driving a Toyota Corolla Altis
            1.6 with 50% NCD, a clean driving record, 10 years of driving
            experience with 0 claims in the last 3 years
          </p>
        )}
        {signUpBonusPromoText?.value && showSignUpBonusCard && (
          <SignUpBonusCard
            description={signUpBonusPromoText.value}
            expiry={signUpBonusExpiry?.value}
          />
        )}
        <div className="flex flex-col items-center w-full gap-4 lg:flex-row lg:items-start lg:gap-4">
          {ratingBreakdown.length > 0 && (
            <div className="bg-neutral-200 p-2 w-full lg:w-80">
              <ProductBreakdownList ratingBreakdown={ratingBreakdown} />
            </div>
          )}
          {userReview && (
            <UserReview user={userReview.user} review={userReview.reviewText} />
          )}
          {isMobile &&
            (isCarInsurance ? (
              <CallToActionCarInsuranceButtons
                internalLink={as}
                externalLink={externalLink}
                price={lowestPrice}
                product={product}
              />
            ) : (
              <CallToActionButtons
                internalLink={as}
                externalLink={externalLink}
                analyticLink={analyticLink}
                slug={productSlug}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default ProductItemWithDetail;
